import { useI18N } from "@customer-tools-web/component-i18n";
import { isMobile, Icon } from "@customer-tools-web/component-ui";
import { Badge, Popover } from "antd";
import * as React from "react";
import { useHistory } from "react-router";
import styled, { createGlobalStyle } from "styled-components";

import Notification from "../components/Notification";

interface NotificationMenuProps {
  braze: any;
  ctServer: string;
}

const NotificationMenu: React.FC<NotificationMenuProps> = (
  props: NotificationMenuProps
): React.ReactElement<void> => {
  const { t } = useI18N();
  const { ctServer, braze } = props;
  const history = useHistory();
  const [notificationVisible, setNotificationVisible] =
    React.useState<boolean>(false);
  const [notificationCount, setNotificationCount] = React.useState<number>(
    braze?.getContentUnreadCount()
  );

  React.useEffect(() => {
    function countListener() {
      setNotificationCount(braze.getContentUnreadCount());
    }
    if (braze) {
      // to cater multiple tab changes is sync
      window.addEventListener("storage", countListener);
      window.addEventListener("refreshContentCardUnRead", countListener);

      return function cleanupListener() {
        window.removeEventListener("storage", countListener);
        window.removeEventListener("refreshContentCardUnRead", countListener);
      };
    }
  }, []);

  const isActive = () => {
    return (
      notificationVisible || location?.pathname === `${ctServer}/notifications`
    );
  };

  const onNotificationIconClick = () => {
    if (isMobile(window.innerWidth)) {
      history.push(`${ctServer}/notifications`);
    }
    return false;
  };

  return (
    <React.Fragment>
      <GlobalStyle />
      {isMobile(window.innerWidth) ? (
        renderNotificationMenuIcon(
          notificationCount,
          onNotificationIconClick,
          isActive()
        )
      ) : (
        <Popover
          title={
            <NotificationWrapper>
              <NotificationTitle>{t("notification.title")}</NotificationTitle>
            </NotificationWrapper>
          }
          content={
            <Notification
              braze={props.braze}
              onClose={() => {
                setNotificationVisible(false);
              }}
            />
          }
          overlayClassName="notification-overlay"
          trigger="click"
          visible={notificationVisible}
          onVisibleChange={setNotificationVisible}
          placement="bottomRight"
        >
          {renderNotificationMenuIcon(
            notificationCount,
            onNotificationIconClick,
            isActive()
          )}
        </Popover>
      )}
    </React.Fragment>
  );
};

const renderNotificationMenuIcon = (
  notificationCount: number,
  onNotificationIconClick: any,
  isActive: boolean
) => (
  <BadgeContainer value={notificationCount} onClick={onNotificationIconClick}>
    <NotificationMenuAnchor
      data-test="notification-anchor"
      count={notificationCount >= 100 ? "99+" : notificationCount}
      dot={false}
    >
      <NotificationIcon
        data-test="notification-icon"
        size={"2.2rem"}
        type={isActive ? "bell-on" : "bell-round"}
        active={isActive}
      />
    </NotificationMenuAnchor>
  </BadgeContainer>
);

const GlobalStyle = createGlobalStyle`
  .ant-popover.notification-overlay {
    width: 47rem;
    left: 0px !important;
    float: right;
    position:relative;
    padding-top:0px;
    > .ant-popover-content > .ant-popover-arrow {
       display: none;
     }
    .ant-popover-inner-content {
      padding: 0px;
      overflow-x: hidden;
      overflow-y: auto;
      max-height: 70vh;
    }
    .ant-popover-content{
      position: relative;
      top: -4px;
     }
  }
`;

const NotificationWrapper = styled.div`
  :not(:first-child) {
    margin-left: 12px;
  }
  overflow: hidden;
`;

const NotificationTitle = styled.span`
  display: block;
  float: left;
  font-size: ${(props) => props?.theme?.fontSize?.title3};
  font-weight: 700;
  line-height: 5.9rem;
  color: ${(props) => props.theme.defaultIconColor};
  text-overflow: ellipsis;
  overflow: hidden;
`;

const BadgeContainer = styled.div<{ value: number }>`
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  margin-right: 24px;
  top: -0.2rem;
`;

const NotificationMenuAnchor = styled(Badge)`
  height: 1.6rem;
  sup {
    transform: translate(70%, -50%);
    background-color: ${(props) => props.theme.pureGreen};
    font-size: ${(props) => props?.theme?.fontSize?.sup};
    height: 16px;
    line-height: 14px;
    padding: 0.5px 5px 0 5px;
    top: 2px;
    font-weight: 600;
  }
`;

const NotificationIcon = styled(Icon)<{ active: boolean }>`
  color: ${(props) =>
    props.active ? props.theme.defaultIconColor : props.theme.textDeepGray};
`;

export default NotificationMenu;
